<template>
  <div class="left-panel" :class="{ collapsed: isCollapsed }" :style="{ left: leftOffset }">
    <div class="panel-controls">
      <div v-if="!isCollapsed" class="view-toggle">
        <button
          @click="viewMode = 'grid'"
          :class="{ active: viewMode === 'grid' }"
          class="toggle-btn"
        >
          <font-awesome-icon :icon="['fas', 'th']" />
        </button>
        <button
          @click="viewMode = 'list'"
          :class="{ active: viewMode === 'list' }"
          class="toggle-btn"
        >
          <font-awesome-icon :icon="['fas', 'list']" />
        </button>
      </div>
      <div class="panel-toggle" @click="togglePanel">
        <button v-if="!isCollapsed" class="dashboard-close-btn" title="Collapse">
          <font-awesome-icon size="lg" icon="times" />
        </button>
        <button v-else class="dashboard-close-btn" title="Expand">
          <font-awesome-icon size="lg" :icon="['fas', 'square-plus']" />
        </button>
      </div>
    </div>

    <!-- Metrics recalculation in progress - cleared by the 30s project poll -->
    <div v-if="projectBusy" class="busy-banner" :title="busyStatus">
      <font-awesome-icon :icon="['fas', 'sync']" spin />
      <span v-if="!isCollapsed" class="busy-text">
        {{ busyStatus }} - dashboards are unavailable until this completes.
      </span>
    </div>

    <div v-if="!isCollapsed" class="panel-header">
      <h3 class="gallery-title">Study Gallery</h3>
      <div class="search-container">
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Search images..."
        />
      </div>
      <div class="dropdown-container">
        <div class="dropdown-group">
          <label class="dropdown-label">Study Status:</label>
          <BFormSelect
            v-model="selectedStatus"
            :options="statusOptions"
            class="dropdown-select"
          />
        </div>
        <div class="dropdown-group">
          <label class="dropdown-label">Subgroups:</label>
          <BFormSelect
            v-model="selectedSubgroup"
            :options="subgroupOptions"
            class="dropdown-select"
          />
        </div>
      </div>

      <!-- Submit ground-truthed studies to the AI (retrospective, admins only) -->
      <div v-if="canSubmitToAI" class="ai-submit-bar">
        <button
          class="ai-submit-btn"
          :disabled="submitting || selectedForAi.size === 0"
          @click="submitToAI"
        >
          <template v-if="submitting">
            <font-awesome-icon :icon="['fas', 'sync']" spin />
            Submitting...
          </template>
          <template v-else>
            Submit {{ selectedForAi.size }}
            {{ selectedForAi.size === 1 ? "study" : "studies" }} to AI
          </template>
        </button>
        <a href="#" class="ai-select-all" @click.prevent="toggleSelectAllEligible">
          {{
            allEligibleSelected
              ? "Clear selection"
              : `Select all eligible (${eligibleImages.length})`
          }}
        </a>
      </div>

      <!-- Pagination controls - only when not collapsed -->
      <div
        v-if="!isCollapsed && filteredImages.length > 0"
        class="pagination-controls"
      >
        <div class="page-size-selector">
          <label class="page-size-label">Per page:</label>
          <BFormSelect
            v-model="pageSize"
            :options="pageSizeOptions"
            class="page-size-select"
          />
        </div>
        <div class="pagination-info">
          <span class="pagination-text">
            Showing {{ (currentPage - 1) * pageSize + 1 }}-{{
              Math.min(currentPage * pageSize, filteredImages.length)
            }}
            of {{ filteredImages.length }} studies
          </span>
        </div>
        <div class="page-navigation">
          <button
            @click="currentPage = 1"
            :disabled="currentPage === 1"
            class="pagination-btn"
            title="First page"
          >
            <font-awesome-icon :icon="['fas', 'angle-double-left']" />
          </button>
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="pagination-btn"
            title="Previous page"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
          </button>
          <span class="page-indicator">
            Page {{ currentPage }} of {{ totalPages }}
          </span>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="pagination-btn"
            title="Next page"
          >
            <font-awesome-icon :icon="['fas', 'chevron-right']" />
          </button>
          <button
            @click="currentPage = totalPages"
            :disabled="currentPage === totalPages"
            class="pagination-btn"
            title="Last page"
          >
            <font-awesome-icon :icon="['fas', 'angle-double-right']" />
          </button>
        </div>
      </div>
    </div>

    <div
      ref="panelContentRef"
      class="panel-content"
      :class="{ 'list-view': viewMode === 'list' }"
    >
      <div v-if="loadingImages" class="loading-state">
        <font-awesome-icon :icon="['fas', 'sync']" spin size="2x" />
        <p v-if="!isCollapsed" class="mt-2">Loading studies...</p>
      </div>
      <div v-else-if="filteredImages.length === 0" class="empty-state">
        <font-awesome-icon
          :icon="['fas', 'image']"
          :size="isCollapsed ? '2x' : '3x'"
          class="mb-3"
          v-if="!isCollapsed"
        />
        <p v-if="!isCollapsed">
          {{
            selectedProjectId
              ? "No studies found"
              : "Select a Project to view studies"
          }}
        </p>
      </div>
      <div
        v-for="item in paginatedImages"
        :key="item.id"
        class="image-item"
        :class="{ selected: isImageSelected(item) }"
        @click="handleItemClick(item)"
      >
        <input
          v-if="canSubmitToAI && item.submittable"
          type="checkbox"
          class="ai-select-checkbox"
          title="Select for AI processing"
          :checked="selectedForAi.has(item.id)"
          @click.stop
          @change="toggleAiSelection(item.id)"
        />
        <!-- Show thumbnail in grid mode when expanded, or always when collapsed -->
        <div
          v-if="isCollapsed || viewMode === 'grid'"
          class="image-placeholder"
        >
          <img
            v-if="item.thumbnailUrl"
            :src="item.thumbnailUrl"
            :alt="item.title"
            :title="item.title"
            class="thumbnail-image"
            loading="lazy"
            @error="handleImageError"
          />
          <font-awesome-icon
            v-else
            :icon="['fas', 'image']"
            class="placeholder-icon"
          />
          <!-- Status badge for grid view only (not collapsed) -->
          <small
            v-if="!isCollapsed && item.status"
            class="status-badge"
            :style="{ backgroundColor: item.statusColor }"
          >
            {{ item.status }}
          </small>
        </div>
        <div v-if="!isCollapsed" class="image-info">
          <div class="image-info-header">
            <h6 class="image-title">{{ item.title }}</h6>
            <span v-if="item.created_at" class="image-time">{{
              formatDate(item.created_at, "yyyy-MM-dd HH:mm")
            }}</span>
          </div>
          <p v-if="item.description" class="image-description">
            {{ item.description }}
          </p>
          <font-awesome-icon
            :icon="['fas', 'heart']"
            class="favourite-icon"
            :class="viewMode === 'grid' ? 'grid' : 'list'"
            v-if="item.favourite"
          />
          <!-- Status badge for list view -->
          <small
            v-if="viewMode === 'list' && item.status"
            class="status-badge-list"
            :style="{ backgroundColor: item.statusColor }"
          >
            {{ item.status }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useStore } from "vuex";
import { BFormSelect } from "bootstrap-vue-next";
import { apiRoute } from "@/helpers/api-route";
import { PERIOD_RANGES } from "@/helpers/period-ranges";
import { IOVThesholds } from "@/helpers/iovthresholds";
import { annotationService } from "@/services/annotation.service";

const store = useStore();

const isCollapsed = computed(
  () => store.state.workspacePanel.leftPanelCollapsed,
);
const rightPanelCollapsed = computed(
  () => store.state.workspacePanel.rightPanelCollapsed,
);
const leftOffset = computed(() =>
  rightPanelCollapsed.value ? 'var(--panel-collapsed-width)' : 'var(--panel-right-width)',
);
const searchQuery = ref("");
const viewMode = ref("grid");
const panelContentRef = ref(null);

// Pagination state
const currentPage = ref(1);
const pageSize = ref(32);

// AI submission selection - declared here because the immediate
// selectedProjectId watcher below resets it during setup
const selectedForAi = ref(new Set());
const submitting = ref(false);

// Get selected project ID and project details
const selectedProjectId = computed(
  () => store.state.projectSubmission.selectedProjectId,
);
const selectedImage = computed(() => store.state.workspacePanel.selectedImage);
const updatedImage = computed(() => store.state.workspacePanel.updatedImage);

watch(
  () => updatedImage.value.id,
  async (newId) => {
    if (newId) {
      if (projectType.value !== "retrospective") {
        const updatedImageIndex = pmsImages.value.findIndex(
          (image) => image.submission._id === newId,
        );
        if (updatedImageIndex !== -1) {
          pmsImages.value.splice(
            updatedImageIndex,
            1,
            updatedImage.value?.value,
          );
          store.dispatch(
            "staffSubmission/updateLocalImage",
            updatedImage.value?.value,
          );
          store.dispatch("workspacePanel/clearUpdatedImage");
        }
      } else {
        const updatedImageAsIs = projectImages.value.find(
          (image) => image._id === newId,
        );
        const labelsAsIs = labelledImages.value.find(
          (image) => image.imageId === newId,
        );
        const _singleLabel = updatedImage.value?.value?.[0] || null;
        if (_singleLabel) {
          const _singleAnnotation = await annotationService.getSpecificImage(
            updatedImageAsIs._id,
            selectedProjectId.value,
          );
          console.log(
            "updatedImage",
            updatedImageAsIs,
            _singleLabel,
            labelsAsIs,
            _singleAnnotation,
          );
          const singleLabel = {
            accuracy:
              _singleLabel.users?.length <= 1
                ? null
                : _singleAnnotation.accuracy.find(
                    (acc) => acc.project === selectedProjectId.value,
                  )?.value,
            ai_accuracy:
              _singleAnnotation.ai_accuracy?.length === 0
                ? null
                : _singleAnnotation.ai_accuracy.find(
                    (acc) => acc.project === selectedProjectId.value,
                  )?.value,
            created_at: _singleAnnotation.created_at,
            diagnoses: _singleLabel.diagnoses.flatMap((label) =>
              Object.keys(label)
                .filter((key) => key !== "created_by")
                .map((key) => label[key]),
            ),
            image: _singleAnnotation.image,
            imageId: _singleAnnotation._id,
            image_set: _singleAnnotation.image_set,
            label_created_at: labelsAsIs?.label_created_at || new Date(),
            metadata: updatedImageAsIs.metadata,
            mpu: labelsAsIs?.mpu || 0,
            pathologies: _singleLabel.pathologies.flatMap((label) =>
              Object.keys(label)
                .filter((key) => key !== "created_by")
                .map((key) => label[key].name),
            ),
            removed: labelsAsIs?.removed || [],
            users: [],
          };

          _singleLabel.users.forEach((user) => {
            const _userPathologies = _singleLabel.pathologies.find(
              (label) => label["created_by"] === user._id,
            );
            console.log("updatedImage _userPathologies", _userPathologies);
            const _user = {
              _id: user._id,
              name: user.personal.fullname,
              role: user.projectRole.find(
                (role) => role.project === selectedProjectId.value,
              )?.role,
              pathologiesCount: Object.keys(_userPathologies || {}).length - 1,
              // Same shape labels_reduced serves: every finding name the user
              // recorded, for ground-truth and per-reader figures
              pathologies: [
                ...new Set(
                  Object.keys(_userPathologies || {})
                    .filter((key) => key !== "created_by")
                    .map((key) => _userPathologies[key]?.name)
                    .filter(Boolean),
                ),
              ],
            };
            singleLabel.users.push(_user);
          });
          console.log("updatedImage singleLabel", singleLabel);
          store.dispatch(
            "labelledImagesSubmission/updateLocalLabelledImage",
            singleLabel,
          );

          const _projectImage = {
            accuracy: _singleAnnotation.accuracy,
            ai_accuracy: _singleAnnotation.ai_accuracy,
            crated_at: updatedImageAsIs.created_at,
            image: _singleAnnotation.image,
            image_set: _singleAnnotation.image_set,
            labelled: _singleAnnotation.labelled,
            metadata: updatedImageAsIs.metadata,
            otherImages: updatedImageAsIs.otherImages,
            _id: updatedImageAsIs._id,
          };
          store.dispatch(
            "projectSubmission/updateLocalProjectImage",
            _projectImage,
          );
        }
        store.dispatch("workspacePanel/clearUpdatedImage");
      }
    }
  },
);
const selectedProject = computed(() => {
  const projects = store.state.projectSubmission.projects || [];
  return projects.find((p) => p._id === selectedProjectId.value) || null;
});
const projectType = computed(
  () => selectedProject.value?.type || "retrospective",
);

// `busy` currently only ever means "metrics are being recalculated"
const projectBusy = computed(() => selectedProject.value?.busy === true);
const busyStatus = computed(
  () => selectedProject.value?.status || "Recalculating project metrics",
);

// Get labelled images and AI images for status calculations
const labelledImages = computed(
  () => store.state.labelledImagesSubmission?.labelledImages || [],
);
const aiImages = computed(
  () => store.state.labelledImagesSubmission?.aiImages || [],
);
const members = computed(() => store.state.usersSubmission?.members || []);
const user = computed(() => store.state.auth?.user);

// Role within the selected project. Matches the legacy Run.vue rule: a member
// without an explicit role on this project is treated as an Administrator.
const currentUserRole = computed(() => {
  const userId = user.value?.id || user.value?._id;
  const currentMember = members.value.find((m) => m._id === userId);
  if (!currentMember) return "View Only";
  const projectRole = currentMember.projectRole?.find(
    (r) => r.project === selectedProjectId.value,
  );
  return projectRole ? projectRole.role : "Administrator";
});

// Submitting studies to the AI is retrospective-only and admin-only, and stays
// hidden unless the current filter actually holds studies ready for the AI
const canSubmitToAI = computed(
  () =>
    !isCollapsed.value &&
    projectType.value === "retrospective" &&
    (currentUserRole.value === "Administrator" ||
      currentUserRole.value === "Data Scientist") &&
    eligibleImages.value.length > 0,
);

const currentLabellerImage = computed(
  () => store.state.labelerImageQueue.currentImage,
);

// Prospective project loading state
const pmsLoading = ref(false);
const pmsImages = ref([]);
const pmsCurrentPeriod = ref("count");
const pmsCurrentPage = ref(1);
const pmsTotalCount = ref(0);
const pmsStaffSubmissions = computed(
  () => store.state.staffSubmission.info || {},
);
const pmsLoadState = computed(() => store.state.staffSubmission.status.loaded);

// Get project images from store based on project type
const projectImages = computed(() => {
  if (
    projectType.value === "prospective" ||
    projectType.value === "pros-light" ||
    projectType.value === "retro-light"
  ) {
    return pmsImages.value;
  } else {
    return store.state.projectSubmission.projectImages || [];
  }
});

const loadingImages = computed(() => {
  if (
    projectType.value === "prospective" ||
    projectType.value === "pros-light" ||
    projectType.value === "retro-light"
  ) {
    return pmsLoading.value;
  } else {
    return store.state.projectSubmission.loadingProjectImages;
  }
});

// Dropdown selections
const selectedStatus = ref(null);
const selectedSubgroup = ref(null);

// Helper to get readers for current project
const readers = computed(() => {
  if (!members.value || members.value.length === 0 || !selectedProject.value) {
    return [];
  }
  const projectId = selectedProject.value._id;
  return members.value.filter((member) => {
    const role = member.projectRole?.find((r) => r.project === projectId);
    return role && role.role === "Reader";
  });
});

// Helper to get moderators for current project
const moderators = computed(() => {
  if (!members.value || members.value.length === 0 || !selectedProject.value) {
    return [];
  }
  const projectId = selectedProject.value._id;
  return members.value.filter((member) => {
    const role = member.projectRole?.find((r) => r.project === projectId);
    return role && role.role === "Moderator";
  });
});

// Helper function to calculate study-level agreement
const getStudyAgreementLevel = (image) => {
  if (!image || !selectedProject.value) return null;

  const imageId = image._id;
  const projectId = selectedProject.value._id;

  // Find labelled image for current image
  // The labelledImages array contains aggregated study-level accuracy
  const labelledImage = labelledImages.value.find(
    (li) => li.imageId === imageId,
  );

  if (!labelledImage) return null;

  // Check if we have all required readers
  const readerCount =
    labelledImage.users?.filter((u) => u.role === "Reader").length || 0;
  const requiredReaders = readers.value.length;

  if (readerCount < requiredReaders || requiredReaders === 0) {
    return "not-complete";
  }

  // A project with one reader has no second opinion to measure against, so the
  // API records no variability for these studies and the figure below cannot be
  // read as agreement - it would put every one of them in full disagreement.
  // That reader's annotation is the ground truth on its own.
  if (requiredReaders === 1) {
    return "full-agreement";
  }

  // Get the aggregated study-level accuracy (already averaged by the API)
  const accuracy = labelledImage.accuracy || 0;

  // Convert to variability (IOV)
  const variability = 1 - accuracy;
  const thresholds = IOVThesholds();

  // Return agreement level based on variability
  if (variability >= thresholds.partialAgreement) {
    return "full-disagreement";
  }
  if (variability >= thresholds.fullAgreement) {
    return "partial-agreement";
  }
  return "full-agreement";
};

// Full disagreement between moderators of the same study, using the same
// thresholds as the reader logic above. Needs a moderator-only accuracy from
// the API - the aggregated `accuracy` blends every annotator regardless of
// role, so it cannot answer this on its own.
const hasModeratorDisagreement = (image) => {
  const labelledImage = labelledImages.value.find(
    (li) => li.imageId === image._id,
  );
  if (!labelledImage || labelledImage.moderator_accuracy == null) return false;

  // Disagreement only means anything once a second moderator has annotated
  const moderatorCount =
    labelledImage.users?.filter((u) => u.role === "Moderator").length || 0;
  if (moderatorCount < 2) return false;

  const variability = 1 - labelledImage.moderator_accuracy;
  return variability >= IOVThesholds().partialAgreement;
};

// Unified status calculation function with priority logic
// Priority: AI statuses > Moderator disagreement > Moderated > Agreement levels > Basic annotation
const calculateImageStatus = (image) => {
  if (!image || !selectedProject.value) {
    return { status: "Not Annotated", color: "#14304ced" };
  }

  const imageId = image._id;
  const projectId = selectedProject.value._id;

  // Find labelled image data (for agreement calculations)
  const labelledImage = labelledImages.value.find(
    (li) => li.imageId === imageId,
  );

  // Find AI image data
  const aiImage = aiImages.value.find((ai) => ai.imageId === imageId);

  // TIER 1: AI Statuses (Highest Priority)
  if (aiImage) {
    const aiDiagnoses = new Set(
      [...(aiImage.diagnoses || []), aiImage.diagnosis].filter(Boolean),
    );

    if (aiDiagnoses.has("Error")) {
      return { status: "Error", color: "red" };
    }
    if (aiDiagnoses.has("Unknown")) {
      return { status: "Unknown", color: "red" };
    }
    if (aiDiagnoses.size > 0) {
      return { status: "Analyzed", color: "green" };
    }
  }

  // TIER 2: Moderation Status
  if (labelledImage) {
    if (hasModeratorDisagreement(image)) {
      return { status: "Moderator Disagreement", color: "red" };
    }

    const hasModeratorAnnotation = labelledImage.users?.some(
      (u) => u.role === "Moderator",
    );
    if (hasModeratorAnnotation) {
      return { status: "Moderated", color: "purple" };
    }

    // TIER 3: Agreement Levels
    const agreementLevel = getStudyAgreementLevel(image);
    if (agreementLevel === "full-disagreement") {
      return { status: "Full Disagreement", color: "red" };
    }
    if (agreementLevel === "partial-agreement") {
      return { status: "Partial Agreement", color: "orange" };
    }
    if (agreementLevel === "full-agreement") {
      return { status: "Full Agreement", color: "green" };
    }

    // Partially Annotated - not all readers have annotated
    const readerCount =
      labelledImage.users?.filter((u) => u.role === "Reader").length || 0;
    const requiredReaders = readers.value.length;
    if (readerCount > 0 && readerCount < requiredReaders) {
      return { status: "Partially Annotated", color: "orange" };
    }
  }

  // TIER 4: Report Extraction (GT extracted from an uploaded radiology report)
  const hasExtractedReport = image.reports?.some(
    (r) => r.project === projectId && r.gt_written,
  );
  if (hasExtractedReport) {
    return { status: "Report Extracted", color: "green" };
  }

  // TIER 5: Basic Annotation Status (Lowest Priority)
  const hasAnnotation =
    image.accuracy?.find((a) => a.project === projectId) || image.labels;
  if (hasAnnotation) {
    return { status: "Annotated", color: "green" };
  }

  return { status: "Not Annotated", color: "#14304ced" };
};

// Prospective status calculation function with priority logic
// Priority: Study moderated > Agreement levels > Report status > Analysis status
const calculateProspectiveStatus = (submission) => {
  if (!submission) {
    return { status: "Not yet reported", color: "orange" };
  }

  const hasReport = !!submission.report;
  const reportCompleted = submission.report?.status === "Completed";
  const hasReportAnalysis = !!submission.reportAnalysis;
  const analysisCompleted = submission.reportAnalysis?.status === "Completed";
  const hasAIResponse = submission.submission?.attachments?.[0]?.response;
  const hasFeedback = submission.submission?.attachments?.[0]?.feedback;

  // TIER 1: Study moderated (Highest Priority)
  if (hasFeedback) {
    return { status: "Study moderated", color: "purple" };
  }

  // TIER 2: Agreement Levels (if analysis completed)
  if (analysisCompleted && hasReportAnalysis) {
    const accuracy = 1 - (submission.reportAnalysis.accuracy || 0);
    const thresholds = IOVThesholds();

    if (accuracy <= thresholds.fullAgreement) {
      return { status: "Full agreement", color: "green" };
    }
    if (accuracy <= thresholds.partialAgreement) {
      return { status: "Partial agreement", color: "orange" };
    }
    return { status: "Full disagreement", color: "red" };
  }

  // TIER 3: Report Status
  if (hasReport) {
    if (reportCompleted) {
      return { status: "Report recorded", color: "green" };
    }
    return { status: "Report not completed", color: "purple" };
  }

  // TIER 4: Analysis Status
  if (!hasAIResponse) {
    return { status: "AI results not available", color: "red" };
  }

  if (hasReportAnalysis && !analysisCompleted) {
    return { status: "Not yet analysed", color: "purple" };
  }

  // TIER 5: Default
  return { status: "Not yet reported", color: "orange" };
};

// Check if a prospective submission matches specific status criteria (for filtering)
const prospectiveMatchesStatus = (submission, statusValue) => {
  if (!submission) return false;

  const hasReport = !!submission.report;
  const reportCompleted = submission.report?.status === "Completed";
  const hasReportAnalysis = !!submission.reportAnalysis;
  const analysisCompleted = submission.reportAnalysis?.status === "Completed";
  const hasAIResponse = submission.submission?.attachments?.[0]?.response;
  const hasFeedback = submission.submission?.attachments?.[0]?.feedback;

  switch (statusValue) {
    case "study-moderated":
      return hasFeedback;

    case "full-agreement":
      if (!analysisCompleted || !hasReportAnalysis) return false;
      return (
        1 - (submission.reportAnalysis.accuracy || 0) <=
        IOVThesholds().fullAgreement
      );

    case "partial-agreement":
      if (!analysisCompleted || !hasReportAnalysis) return false;
      const accuracy = 1 - (submission.reportAnalysis.accuracy || 0);
      const thresholds = IOVThesholds();
      return (
        accuracy > thresholds.fullAgreement &&
        accuracy <= thresholds.partialAgreement
      );

    case "full-disagreement":
      if (!analysisCompleted || !hasReportAnalysis) return false;
      return (
        1 - (submission.reportAnalysis.accuracy || 0) >
        IOVThesholds().partialAgreement
      );

    case "report-recorded":
      return hasReport && reportCompleted;

    case "report-not-completed":
      return hasReport && !reportCompleted;

    case "ai-not-available":
      return !hasAIResponse;

    case "not-yet-analysed":
      return hasReportAnalysis && !analysisCompleted;

    case "not-yet-reported":
      return !hasReport;

    default:
      return false;
  }
};

const prospectiveMatchesSubgroup = (_submission, subgroupValue) => {
  if (!_submission || !selectedProject.value) return false;
  if (
    !selectedProject.value?.subgroups ||
    selectedProject.value?.subgroups.length === 0
  )
    return false;

  const submission = _submission.submission;
  const _filter = selectedProject.value?.subgroups.find(
    (s) => s.name === subgroupValue,
  ).definiation;
  let includeInFilter = false;

  const _pathologies = [];
  const _diagnoses = [];
  if (submission && submission.attachments && submission.attachments[0]) {
    const _response = submission.attachments[0].response;
    if (_response && _response.labels && _response.labels[0]) {
      _response.labels[0].pathologies.forEach((p) => {
        _pathologies.push(
          p.predicted_diagnosis.replace(/[^a-zA-Z]/g, "").toLowerCase(),
        );
        if (p.isDiagnosis) {
          _diagnoses.push(
            p.predicted_diagnosis.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          );
        }
      });
    }
  }

  const image = {
    details: {
      pathologies: _pathologies,
      diagnoses: _diagnoses,
      tags: [],
      meta: submission
        ? submission.metadata.filter(
            (p) => p.project === selectedProject.value?._id,
          )
        : [],
    },
  };
  console.log(image.details);

  Object.keys(_filter).forEach((_key) => {
    if (_key === "Pathologies") {
      _filter[_key].forEach((path) => {
        if (
          image.details.pathologies.indexOf(
            path.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    }
    if (_key === "Tags") {
      _filter[_key].forEach((tag) => {
        if (
          image.details.tags.indexOf(
            tag.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    } else if (_key === "Diagnosis") {
      _filter[_key].forEach((dia) => {
        if (
          image.details.diagnoses.indexOf(
            dia.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    } else if (
      image.details.meta &&
      image.details.meta[0] &&
      image.details.meta[0] &&
      image.details.meta[0].values.find((_meta) => _meta.name === _key) &&
      _filter[_key].includes(
        image.details.meta[0].values.find((_meta) => _meta.name === _key).value,
      )
    ) {
      includeInFilter = true;
    }
  });

  return includeInFilter;
};

// Check if an image matches specific status criteria (for filtering)
// This allows images to appear in multiple filters they qualify for
const imageMatchesStatus = (image, statusValue) => {
  if (!image || !selectedProject.value) return false;

  const imageId = image._id;
  const projectId = selectedProject.value._id;
  const labelledImage = labelledImages.value.find(
    (li) => li.imageId === imageId,
  );
  const aiImage = aiImages.value.find((ai) => ai.imageId === imageId);

  switch (statusValue) {
    // AI Statuses
    case "error":
      if (!aiImage) return false;
      return new Set(
        [...(aiImage.diagnoses || []), aiImage.diagnosis].filter(Boolean),
      ).has("Error");

    case "unknown":
      if (!aiImage) return false;
      return new Set(
        [...(aiImage.diagnoses || []), aiImage.diagnosis].filter(Boolean),
      ).has("Unknown");

    case "analyzed":
      if (!aiImage) return false;
      const aiDiagnoses = new Set(
        [...(aiImage.diagnoses || []), aiImage.diagnosis].filter(Boolean),
      );
      return (
        aiDiagnoses.size > 0 &&
        !aiDiagnoses.has("Error") &&
        !aiDiagnoses.has("Unknown")
      );

    // Moderation Status
    case "moderator-disagreement":
      return hasModeratorDisagreement(image);

    case "moderated":
      if (!labelledImage) return false;
      return labelledImage.users?.some((u) => u.role === "Moderator");

    // Agreement Levels
    case "full-disagreement":
      return getStudyAgreementLevel(image) === "full-disagreement";

    case "partial-agreement":
      return getStudyAgreementLevel(image) === "partial-agreement";

    case "full-agreement":
      return getStudyAgreementLevel(image) === "full-agreement";

    case "partially-annotated":
      if (!labelledImage) return false;
      const readerCountPartialAnnot =
        labelledImage.users?.filter((u) => u.role === "Reader").length || 0;
      const requiredReadersPartialAnnot = readers.value.length;
      return (
        readerCountPartialAnnot > 0 &&
        readerCountPartialAnnot < requiredReadersPartialAnnot
      );

    // Basic Annotation
    case "annotated":
      return !!(
        image.accuracy?.find((a) => a.project === projectId) || image.labels
      );

    case "report-extracted":
      return image.reports?.some(
        (r) => r.project === projectId && r.gt_written,
      );

    case "not-annotated":
      return !(
        image.accuracy?.find((a) => a.project === projectId) || image.labels
      );

    default:
      return false;
  }
};

const imageMatchesSubgroup = (_image, subgroupValue) => {
  if (!_image || !selectedProject.value) return false;
  if (
    !selectedProject.value?.subgroups ||
    selectedProject.value?.subgroups.length === 0
  )
    return false;

  const imageId = _image._id;
  const labelledImage = labelledImages.value.find(
    (li) => li.imageId === imageId,
  );
  const _filter = selectedProject.value?.subgroups.find(
    (s) => s.name === subgroupValue,
  ).definiation;
  let includeInFilter = false;

  const image = {
    details: {
      pathologies:
        labelledImage && labelledImage.pathologies && labelledImage.pathologies.length > 0
          ? labelledImage.pathologies.map((p) =>
              p.replace(/[^a-zA-Z]/g, "").toLowerCase(),
            )
          : [],
      diagnoses:
        labelledImage && labelledImage.diagnoses && labelledImage.diagnoses.length > 0
          ? labelledImage.diagnoses.map((d) =>
              d.replace(/[^a-zA-Z]/g, "").toLowerCase(),
            )
          : [],
      tags: [],
      meta: _image.metadata.filter(
        (p) => p.project === selectedProject.value?._id,
      ),
    },
  };

  Object.keys(_filter).forEach((_key) => {
    if (_key === "Pathologies") {
      _filter[_key].forEach((path) => {
        if (
          image.details.pathologies.indexOf(
            path.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    }
    if (_key === "Tags") {
      _filter[_key].forEach((tag) => {
        if (
          image.details.tags.indexOf(
            tag.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    } else if (_key === "Diagnosis") {
      _filter[_key].forEach((dia) => {
        if (
          image.details.diagnoses.indexOf(
            dia.replace(/[^a-zA-Z]/g, "").toLowerCase(),
          ) >= 0
        ) {
          includeInFilter = true;
        }
      });
    } else if (
      image.details.meta &&
      image.details.meta[0] &&
      image.details.meta[0] &&
      image.details.meta[0].values.find((_meta) => _meta.name === _key) &&
      _filter[_key].includes(
        image.details.meta[0].values.find((_meta) => _meta.name === _key).value,
      )
    ) {
      includeInFilter = true;
    }
  });

  return includeInFilter;
};

// Dropdown options - will be populated from project data
const statusOptions = computed(() => {
  const options = [{ value: null, text: "All Statuses" }];

  if (
    projectType.value === "prospective" ||
    projectType.value === "pros-light" ||
    projectType.value === "retro-light"
  ) {
    // Prospective status options - all 9 statuses
    // Tier 1: Study moderated
    options.push({ value: "study-moderated", text: "Study moderated" });
    // Tier 2: Agreement Levels
    options.push(
      { value: "full-agreement", text: "Full agreement" },
      { value: "partial-agreement", text: "Partial agreement" },
      { value: "full-disagreement", text: "Full disagreement" },
    );
    // Tier 3: Report Status
    options.push(
      { value: "report-recorded", text: "Report recorded" },
      { value: "report-not-completed", text: "Report not completed" },
    );
    // Tier 4: Analysis Status
    options.push(
      { value: "ai-not-available", text: "AI results not available" },
      { value: "not-yet-analysed", text: "Not yet analysed" },
    );
    // Tier 5: Default
    options.push({ value: "not-yet-reported", text: "Not yet reported" });
  } else {
    // Retrospective status options - all statuses combined
    // Tier 1: AI Statuses
    options.push(
      { value: "error", text: "Error" },
      { value: "unknown", text: "Unknown" },
      { value: "analyzed", text: "Analyzed" },
    );
    // Tier 2: Moderation
    options.push(
      { value: "moderator-disagreement", text: "Moderator Disagreement" },
      { value: "moderated", text: "Moderated" },
    );
    // Tier 3: Agreement Levels
    options.push(
      { value: "full-disagreement", text: "Full Disagreement" },
      { value: "partial-agreement", text: "Partial Agreement" },
      { value: "full-agreement", text: "Full Agreement" },
      { value: "partially-annotated", text: "Partially Annotated" },
    );
    // Tier 4: Basic Annotation
    options.push(
      { value: "report-extracted", text: "Report Extracted" },
      { value: "annotated", text: "Annotated" },
      { value: "not-annotated", text: "Not Annotated" },
    );
  }

  return options;
});

const subgroupOptions = computed(() => {
  const options = [{ value: null, text: "All Subgroups" }];
  if (
    selectedProject.value &&
    selectedProject.value?.subgroups &&
    selectedProject.value?.subgroups.length > 0
  ) {
    selectedProject.value?.subgroups.forEach((s) => {
      options.push({ value: s.name, text: s.name });
    });
  }
  return options;
});

// Prospective image loading functions
const loadProspectiveImages = async (projectId) => {
  if (!projectId) return;

  pmsLoading.value = true;
  pmsImages.value = [];
  store.commit("staffSubmission/clearAllImages");
  pmsCurrentPeriod.value = "count";
  pmsCurrentPage.value = 1;
  pmsTotalCount.value = 0;

  // Start by getting the count
  await store.dispatch("staffSubmission/getCount", projectId);
};

// Recent periods are fetched in a single page; only 'older' paginates.
const PMS_RECENT_PAGE_SIZE = 500;
const PMS_OLDER_PAGE_SIZE = 500;

const loadNextPmsPeriod = async (projectId) => {
  const periods = ["now", "earlier", "yesterday", "week"];

  if (pmsCurrentPeriod.value === "count") {
    // Count was loaded, now load first period
    pmsTotalCount.value = pmsStaffSubmissions.value.count || 0;
    pmsCurrentPeriod.value = "now";
    await store.dispatch("staffSubmission/getByDateRange", {
      period: "now",
      ...PERIOD_RANGES.now(),
      pageSize: PMS_RECENT_PAGE_SIZE,
      project: projectId,
    });
  } else if (periods.includes(pmsCurrentPeriod.value)) {
    // Add current period images
    const periodImages =
      pmsStaffSubmissions.value[pmsCurrentPeriod.value] || [];
    pmsImages.value = [...pmsImages.value, ...periodImages];
    store.commit("staffSubmission/setAllImages", pmsImages.value);

    // Move to next period
    const currentIndex = periods.indexOf(pmsCurrentPeriod.value);
    if (currentIndex < periods.length - 1) {
      pmsCurrentPeriod.value = periods[currentIndex + 1];
      await store.dispatch("staffSubmission/getByDateRange", {
        period: pmsCurrentPeriod.value,
        ...PERIOD_RANGES[pmsCurrentPeriod.value](),
        pageSize: PMS_RECENT_PAGE_SIZE,
        project: projectId,
      });
    } else {
      // Move to 'older' with pagination
      pmsCurrentPeriod.value = "older";
      pmsCurrentPage.value = 1;
      await store.dispatch("staffSubmission/getByDateRange", {
        period: "older",
        ...PERIOD_RANGES.older(1),
        pageSize: PMS_OLDER_PAGE_SIZE,
        project: projectId,
      });
    }
  } else if (pmsCurrentPeriod.value === "older") {
    // Add older images
    const olderImages = pmsStaffSubmissions.value.older || [];
    pmsImages.value = [...pmsImages.value, ...olderImages];
    store.commit("staffSubmission/setAllImages", pmsImages.value);

    // Check if we need more pages or we're done
    if (
      pmsImages.value.length < pmsTotalCount.value &&
      olderImages.length > 0
    ) {
      pmsCurrentPage.value++;
      await store.dispatch("staffSubmission/getByDateRange", {
        period: "older",
        ...PERIOD_RANGES.older(pmsCurrentPage.value),
        pageSize: PMS_OLDER_PAGE_SIZE,
        project: projectId,
      });
    } else {
      // All images loaded
      pmsLoading.value = false;
      console.log("All prospective images loaded:", pmsImages.value.length);
    }
  }
};

// Watch for staffSubmission updates
watch(pmsLoadState, (newState) => {
  if (
    newState &&
    selectedProjectId.value &&
    (projectType.value === "prospective" ||
      projectType.value === "pros-light" ||
      projectType.value === "retro-light")
  ) {
    nextTick(() => {
      loadNextPmsPeriod(selectedProjectId.value);
    });
  }
});

// Watch for project selection and fetch images
watch(
  selectedProjectId,
  async (newProjectId, oldProjectId) => {
    selectedForAi.value = new Set();
    if (newProjectId && newProjectId !== oldProjectId) {
      console.log(
        "Fetching images for project:",
        newProjectId,
        "Type:",
        projectType.value,
      );

      // Reset pagination when project changes
      currentPage.value = 1;

      if (
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
      ) {
        // Load prospective images
        await loadProspectiveImages(newProjectId);
      } else {
        // Load retrospective images and all related data
        await Promise.all([
          store.dispatch("projectSubmission/getProjectImages2", newProjectId),
          store.dispatch(
            "labelledImagesSubmission/fetchLabelledImages2",
            newProjectId,
          ),
          store.dispatch(
            "labelledImagesSubmission/fetchAILabelledImages",
            newProjectId,
          ),
          store.dispatch("projectSubmission/getRejections2", newProjectId),
          store.dispatch("projectSubmission/getProjectStats", newProjectId),
          store.dispatch(
            "projectSubmission/getProjectDetailedStats",
            newProjectId,
          ),
          store.dispatch("usersSubmission/getMembers", newProjectId),
        ]);
      }
    } else if (!newProjectId && oldProjectId) {
      // Home selected - clear left panel
      console.log("Home selected - clearing left panel");

      // Reset pagination
      currentPage.value = 1;

      // Clear prospective images
      pmsImages.value = [];
      store.commit("staffSubmission/clearAllImages");
      pmsLoading.value = false;
      pmsCurrentPeriod.value = "count";
      pmsCurrentPage.value = 1;
      pmsTotalCount.value = 0;

      // Clear retrospective images by setting empty array in store
      store.commit("projectSubmission/setProjectImages", []);

      // Reset search and filters
      searchQuery.value = "";
      selectedStatus.value = null;
      selectedSubgroup.value = null;
    }
  },
  { immediate: true },
);

// Ground-truthed and not yet analysed - mirrors the legacy Run.vue rules.
// 'analyzed' excludes Error/Unknown, so failed AI runs stay re-submittable.
const isSubmittable = (image) =>
  (imageMatchesStatus(image, "moderated") ||
    getStudyAgreementLevel(image) === "full-agreement") &&
  !hasModeratorDisagreement(image) &&
  !imageMatchesStatus(image, "analyzed");

// Map project images to display format
const imageItems = computed(() => {
  if (!projectImages.value || projectImages.value.length === 0) {
    return [];
  }

  // Different mapping for prospective vs retrospective
  if (
    projectType.value === "prospective" ||
    projectType.value === "pros-light" ||
    projectType.value === "retro-light"
  ) {
    // Prospective images have submission.attachments structure
    return projectImages.value.map((submission) => {
      const firstAttachment = submission.submission?.attachments?.[0];
      const hasReport = !!submission.report;
      const hasAnalysis = submission.reportAnalysis?.status === "Completed";

      // Get image name using same logic as AnnotationImages.vue
      let imageName = "Unknown";
      if (submission.submission) {
        if (submission.submission.patient) {
          imageName = submission.submission.patient.identifier;
        } else if (submission.submission.study_id) {
          imageName = submission.submission.study_id;
        } else if (firstAttachment) {
          imageName = firstAttachment.name;
        }
      }

      // Get thumbnail path using same logic as AnnotationImages.vue
      let thumbnailUrl = null;
      if (firstAttachment?.path) {
        if (firstAttachment.path.indexOf("http") < 0) {
          thumbnailUrl = `${apiRoute()}${firstAttachment.path}?thumb=yes`;
        } else {
          thumbnailUrl = `${firstAttachment.path}?thumb=yes`;
        }
      }

      // Calculate status for this prospective submission
      const submissionStatus = calculateProspectiveStatus(submission);

      return {
        id: submission.submission._id,
        title: imageName,
        description: "", // Status shown in badge
        thumbnailUrl,
        isAnnotated: hasAnalysis,
        status: submissionStatus.status,
        statusColor: submissionStatus.color,
        created_at: submission.submission.created_at,
        favourite: submission.submission.favourite,
        rawData: submission,
      };
    });
  } else {
    // Retrospective images
    return projectImages.value.map((image) => {
      const imageStatus = calculateImageStatus(image);
      return {
        id: image._id,
        title: image.image_set?.name || image.image?.name || "Untitled",
        description: image.image_set?.sequence || "", // Status shown in badge
        thumbnailUrl: `${apiRoute()}/image/${image._id}?thumb=yes`,
        isAnnotated: !!image.labels,
        status: imageStatus.status,
        statusColor: imageStatus.color,
        created_at: image.created_at,
        favourite: false,
        submittable: isSubmittable(image),
        rawData: image,
      };
    });
  }
});

// Filter images based on search and filters
const filteredImages = computed(() => {
  let filtered = imageItems.value;

  // Search filter
  if (searchQuery.value) {
    const search = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (img) =>
        img.title.toLowerCase().includes(search) ||
        img.description.toLowerCase().includes(search),
    );
  }

  // Status filter
  if (selectedStatus.value) {
    filtered = filtered.filter((img) => {
      if (
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
      ) {
        // Prospective filters - check if submission matches the status criteria
        // This allows images to appear in multiple filters they qualify for
        return prospectiveMatchesStatus(img.rawData, selectedStatus.value);
      } else {
        // Retrospective filters - check if image matches the status criteria
        // This allows images to appear in multiple filters they qualify for
        return imageMatchesStatus(img.rawData, selectedStatus.value);
      }
    });
  }

  if (selectedSubgroup.value) {
    filtered = filtered.filter((img) => {
      if (
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
      ) {
        // Prospective filters - check if submission matches the status criteria
        // This allows images to appear in multiple filters they qualify for
        return prospectiveMatchesSubgroup(img.rawData, selectedSubgroup.value);
      } else {
        // Retrospective filters - check if image matches the status criteria
        // This allows images to appear in multiple filters they qualify for
        return imageMatchesSubgroup(img.rawData, selectedSubgroup.value);
      }
    });
  }

  return filtered;
});

const eligibleImages = computed(() =>
  filteredImages.value.filter((img) => img.submittable),
);

const allEligibleSelected = computed(
  () =>
    eligibleImages.value.length > 0 &&
    eligibleImages.value.every((img) => selectedForAi.value.has(img.id)),
);

const toggleAiSelection = (imageId) => {
  if (selectedForAi.value.has(imageId)) {
    selectedForAi.value.delete(imageId);
  } else {
    selectedForAi.value.add(imageId);
  }
};

// Selects everything eligible in the current filter, not just the current page
const toggleSelectAllEligible = () => {
  if (allEligibleSelected.value) {
    selectedForAi.value = new Set();
  } else {
    selectedForAi.value = new Set(eligibleImages.value.map((img) => img.id));
  }
};

const submitToAI = async () => {
  if (selectedForAi.value.size === 0 || submitting.value) return;
  submitting.value = true;
  // ponytail: one POST for the whole selection, as the legacy UI did.
  // Chunk it here if select-all on large projects starts timing out.
  const error = await store.dispatch("projectSubmission/submitProjectImages", {
    projectId: selectedProjectId.value,
    payload: { images: [...selectedForAi.value] },
  });
  submitting.value = false;
  // The action returns the error instead of throwing, so check the result
  if (error) {
    store.dispatch("alert/error", "Failed to submit images to AI", {
      root: true,
    });
    return;
  }
  store.dispatch("alert/success", "Images submitted for AI processing.", {
    root: true,
  });
  selectedForAi.value = new Set();
  store.dispatch(
    "labelledImagesSubmission/fetchAILabelledImages",
    selectedProjectId.value,
  );
};

// Pagination computed properties
const totalPages = computed(() =>
  Math.ceil(filteredImages.value.length / pageSize.value),
);

const paginatedImages = computed(() => {
  const startIndex = (currentPage.value - 1) * pageSize.value;
  const endIndex = startIndex + pageSize.value;
  return filteredImages.value.slice(startIndex, endIndex);
});

const formatDate = (date, format) => {
  if (!date) return "N/A";

  const d = new Date(date);
  if (isNaN(d.getTime())) return "Invalid Date";

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  if (format === "yyyy-MM-dd HH:mm") {
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  return d.toLocaleString();
};

// Reset to page 1 when filters change
watch([searchQuery, selectedStatus, selectedSubgroup, pageSize], () => {
  currentPage.value = 1;
});

watch(currentLabellerImage, () => {
  if (currentLabellerImage.value) {
    console.log("currentLabellerImage", currentLabellerImage.value);
    const currentImageIndex = filteredImages.value.findIndex(
      (img) => img.id === currentLabellerImage.value._id,
    );
    if (currentImageIndex !== -1) {
      const newPage = Math.ceil((currentImageIndex + 1) / pageSize.value);

      const newItem = filteredImages.value[currentImageIndex];
      const newImageType =
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
          ? "prospective"
          : "retrospective";

      currentPage.value = newPage;
      store.dispatch("workspacePanel/setSelectedImage", {
        image: newItem.rawData,
        imageType: newImageType,
        imageIndex: currentImageIndex,
        totalImages: filteredImages.value.length,
      });
    }
  }
});

// Page size options
const pageSizeOptions = [
  { value: 16, text: "16" },
  { value: 32, text: "32" },
  { value: 64, text: "64" },
  { value: 128, text: "128" },
];

const togglePanel = () => {
  store.dispatch("workspacePanel/toggleLeftPanel");
};

const handleItemClick = (item) => {
  if (currentLabellerImage.value) {
    store.dispatch("workspacePanel/exitLabelerMode");
  }

  // Determine image type based on project type
  const imageType =
    projectType.value === "prospective" ||
    projectType.value === "pros-light" ||
    projectType.value === "retro-light"
      ? "prospective"
      : "retrospective";

  // Find current image index in filtered images
  const itemId =
    imageType === "retrospective" ? item.id : item.rawData?.submission?._id;
  const currentIndex = filteredImages.value.findIndex((img) =>
    imageType === "retrospective"
      ? img.id === itemId
      : img.rawData?.submission?._id === itemId,
  );
  console.log("Found index for clicked item:", currentIndex);

  // Dispatch selected image to store with navigation info
  store.dispatch("workspacePanel/setSelectedImage", {
    image: item.rawData,
    imageType,
    imageIndex: currentIndex,
    totalImages: filteredImages.value.length,
  });

  console.log(
    "Selected image type:",
    imageType,
    "Image data:",
    item.rawData,
    "Index:",
    currentIndex,
    "Total:",
    filteredImages.value.length,
  );
};

const handleImageError = (event) => {
  console.error("Failed to load image:", event.target.src);
  // Hide the failed image and show placeholder icon
  event.target.style.display = "none";
};

const isImageSelected = (item) => {
  // Debug logging - always log for first few items to see structure
  if (!window.debugImageSelectionLogged) {
    console.log("Debug isImageSelected:", {
      selectedImage: selectedImage.value,
      selectedId: selectedImage.value?._id,
      item: item,
      itemId: item.id,
      itemRawDataId: item.rawData?._id,
    });
    window.debugImageSelectionLogged = true;
  }

  const isSelected =
    projectType.value === "retrospective"
      ? selectedImage.value?._id === item.id
      : selectedImage.value?.submission._id === item.rawData.submission._id;
  return isSelected;
};

// Handle navigation events from DataAnnotations components
const handlePreviousImageNavigation = () => {
  console.log("Previous navigation triggered");

  // Find current selected image in filtered images
  const currentImageId =
    projectType.value === "retrospective"
      ? selectedImage.value?._id
      : selectedImage.value?.submission?._id;

  const currentImage = filteredImages.value.find((img) =>
    projectType.value === "retrospective"
      ? img.id === currentImageId
      : img.rawData?.submission?._id === currentImageId,
  );

  if (!currentImage) {
    console.log("No current image found, selecting first");
    // No current image, select the first one
    if (filteredImages.value.length > 0) {
      const firstItem = filteredImages.value[0];
      const newIndex = 0;
      const newImageType =
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
          ? "prospective"
          : "retrospective";

      // Direct store update to avoid recursion
      store.dispatch("workspacePanel/setSelectedImage", {
        image: firstItem.rawData,
        imageType: newImageType,
        imageIndex: newIndex,
        totalImages: filteredImages.value.length,
      });
    }
    return;
  }

  const currentIndex = filteredImages.value.indexOf(currentImage);
  console.log(
    "Current index:",
    currentIndex,
    "Total images:",
    filteredImages.value.length,
  );

  // Navigate to previous image
  if (currentIndex > 0) {
    const previousItem = filteredImages.value[currentIndex - 1];
    if (previousItem) {
      const newIndex = currentIndex - 1;
      const newImageType =
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
          ? "prospective"
          : "retrospective";

      // Handle pagination shift
      const pageStartIndex = (currentPage.value - 1) * pageSize.value;
      if (newIndex < pageStartIndex) {
        currentPage.value--;
      }

      // Direct store update to avoid recursion and ensure index is correct
      store.dispatch("workspacePanel/setSelectedImage", {
        image: previousItem.rawData,
        imageType: newImageType,
        imageIndex: newIndex,
        totalImages: filteredImages.value.length,
      });
    }
  }
};

const handleNextImageNavigation = () => {
  console.log("Next navigation triggered");

  // Find current selected image in filtered images
  const currentImageId =
    projectType.value === "retrospective"
      ? selectedImage.value?._id
      : selectedImage.value?.submission?._id;

  const currentImage = filteredImages.value.find((img) =>
    projectType.value === "retrospective"
      ? img.id === currentImageId
      : img.rawData?.submission?._id === currentImageId,
  );

  if (!currentImage) {
    console.log("No current image found, selecting first");
    // No current image, select the first one
    if (filteredImages.value.length > 0) {
      const firstItem = filteredImages.value[0];
      const newIndex = 0;
      const newImageType =
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
          ? "prospective"
          : "retrospective";

      // Direct store update to avoid recursion
      store.dispatch("workspacePanel/setSelectedImage", {
        image: firstItem.rawData,
        imageType: newImageType,
        imageIndex: newIndex,
        totalImages: filteredImages.value.length,
      });
    }
    return;
  }

  const currentIndex = filteredImages.value.indexOf(currentImage);
  console.log(
    "Current index:",
    currentIndex,
    "Total images:",
    filteredImages.value.length,
  );

  // Navigate to next image
  if (currentIndex < filteredImages.value.length - 1) {
    const nextItem = filteredImages.value[currentIndex + 1];
    if (nextItem) {
      const newIndex = currentIndex + 1;
      const newImageType =
        projectType.value === "prospective" ||
        projectType.value === "pros-light" ||
        projectType.value === "retro-light"
          ? "prospective"
          : "retrospective";

      // Handle pagination shift
      const pageEndIndex = currentPage.value * pageSize.value - 1;
      if (newIndex > pageEndIndex) {
        currentPage.value++;
      }

      // Direct store update to avoid recursion and ensure index is correct
      store.dispatch("workspacePanel/setSelectedImage", {
        image: nextItem.rawData,
        imageType: newImageType,
        imageIndex: newIndex,
        totalImages: filteredImages.value.length,
      });
    }
  }
};

const scrollToSelected = async (delay = 0) => {
  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  await nextTick();
  if (!selectedImage.value) return;

  const selectedId =
    projectType.value === "retrospective"
      ? selectedImage.value._id
      : selectedImage.value.submission?._id;

  if (!selectedId) return;

  // Find index in filteredImages
  const index = filteredImages.value.findIndex((img) => img.id === selectedId);

  if (index !== -1) {
    const targetPage = Math.ceil((index + 1) / pageSize.value);
    if (currentPage.value !== targetPage) {
      currentPage.value = targetPage;
      await nextTick();
    }

    if (panelContentRef.value) {
      const selectedEl = panelContentRef.value.querySelector(
        ".image-item.selected",
      );
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }
  }
};

watch(
  [selectedImage, viewMode, isCollapsed],
  ([newImg, newView, newCollapsed], [oldImg, oldView, oldCollapsed]) => {
    let delay = 0;
    // If collapse state changed, wait for transition (0.2s) + buffer
    if (newCollapsed !== oldCollapsed) {
      delay = 300;
    }
    // If view mode changed, give a small buffer for layout reflow
    else if (newView !== oldView) {
      delay = 100;
    }
    scrollToSelected(delay);
  },
);

// Lifecycle
onMounted(() => {
  // Add event listeners for navigation
  window.addEventListener(
    "navigatePreviousImage",
    handlePreviousImageNavigation,
  );
  window.addEventListener("navigateNextImage", handleNextImageNavigation);
});

onUnmounted(() => {
  // Remove event listeners
  window.removeEventListener(
    "navigatePreviousImage",
    handlePreviousImageNavigation,
  );
  window.removeEventListener("navigateNextImage", handleNextImageNavigation);
});
</script>

<style scoped>
.left-panel {
  position: fixed;
  top: var(--header-height);
  height: calc(100vh - var(--header-height));
  background-color: var(--color-bg-secondary);
  border-right: 1px solid #1e4f78;
  box-shadow: 2px 0 8px rgba(30, 79, 120, 0.3);
  transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: var(--panel-left-width);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  contain: layout;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.left-panel.collapsed {
  width: 70px;
}

.panel-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  border-bottom: 3px solid var(--color-divider-navy);
  background-color: transparent;
  contain: layout style;
}

.collapsed .panel-controls {
  justify-content: center;
}

.panel-toggle {
  padding: 0.5rem;
  cursor: pointer;
  color: var(--color-text-primary);
  transition: background-color 0.15s ease;
  background-color: transparent;
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: center;
}


.busy-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background-color: rgba(30, 79, 120, 0.35);
  border-bottom: 1px solid var(--color-divider-navy);
  color: var(--color-text-primary);
}

.busy-text {
  font-size: 0.8rem;
  line-height: 1.2;
}

.panel-header {
  padding: 1rem;
  border-bottom: 3px solid var(--color-divider-navy);
  background-color: transparent;
  contain: layout style;
}

.gallery-title {
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: var(--font-weight-extrabold);
  margin: 0 0 1rem 0;
  letter-spacing: var(--letter-spacing-header);
}

.search-container {
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.5rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.9rem;
  transition: border-color 0.2s ease;
}

.search-input::placeholder {
  color: var(--color-text-secondary);
}

.search-input:hover {
  border-color: var(--color-border-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-border-primary);
  box-shadow: var(--shadow-focus);
}

.view-toggle {
  display: flex;
  gap: 0.5rem;
  flex: 0 0 50%;
  width: 50%;
  margin: 0 auto;
}

.toggle-btn {
  flex: 1;
  padding: 0.5rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: var(--font-weight-extrabold);
  transform: translateZ(0);
}

.toggle-btn:hover {
  background-color: var(--color-bg-secondary);
  border-color: var(--color-border-primary);
}

.toggle-btn.active {
  background-color: var(--color-accent-primary);
  border-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
}

.dropdown-container {
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  margin-top: 1rem;
}

.dropdown-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.dropdown-label {
  color: var(--color-text-primary);
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  margin: 0;
}

.dropdown-select {
  width: 100%;
  padding: 0.5rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropdown-select:hover {
  border-color: var(--color-border-primary);
}

.dropdown-select:focus {
  outline: none;
  border-color: var(--color-border-primary);
  box-shadow: var(--shadow-focus);
}

.dropdown-select option {
  background-color: var(--color-bg-secondary);
  color: var(--color-text-primary);
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  align-content: start;
  grid-auto-rows: max-content;
  contain: layout style paint;
  transform: translateZ(0);
  will-change: auto;
}

.loading-state,
.empty-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: var(--color-text-secondary);
  text-align: center;
}

.loading-state svg,
.empty-state svg {
  color: var(--color-accent-primary);
  opacity: 0.6;
}

.loading-state p,
.empty-state p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

/* Custom scrollbar */
.panel-content::-webkit-scrollbar {
  width: var(--scrollbar-width);
}

.panel-content::-webkit-scrollbar-track {
  background: var(--color-scrollbar-track);
}

.panel-content::-webkit-scrollbar-thumb {
  background: var(--color-scrollbar-thumb);
  border-radius: var(--border-radius-small);
}

.panel-content::-webkit-scrollbar-thumb:hover {
  background: var(--color-scrollbar-thumb-hover);
}

/* Pagination controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0.5rem 0rem 0.5rem;
  background-color: transparent;
  contain: layout style;
}

.pagination-info {
  display: flex;
  justify-content: center;
  flex: 1;
}

.pagination-text {
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
  text-align: center;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-label {
  font-size: 0.85rem;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

.page-size-select {
  width: 70px;
  padding: 0.4rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.page-size-select:hover {
  border-color: var(--color-border-primary);
}

.page-size-select:focus {
  outline: none;
  border-color: var(--color-border-primary);
  box-shadow: var(--shadow-focus);
}

.page-navigation {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0.4rem;
  border: var(--border-width-standard) solid var(--color-divider-navy);
  border-radius: var(--border-radius-small);
  background-color: var(--color-bg-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.85rem;
  transform: translateZ(0);
}

.pagination-btn:hover:not(:disabled) {
  background-color: var(--color-bg-secondary);
  border-color: var(--color-border-primary);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: var(--color-text-disabled);
}

.page-indicator {
  font-size: 0.85rem;
  color: var(--color-text-primary);
  font-weight: var(--font-weight-medium);
  padding: 0 0.75rem;
  white-space: nowrap;
  min-width: 80px;
  text-align: center;
}

/* Image grid / list styles (migrated from global styles.css) */
.left-panel:not(.collapsed) .panel-content.list-view {
  grid-template-columns: 1fr;
}

.image-item {
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-shadow: var(--shadow-standard);
  box-sizing: border-box;
  position: relative;
  transform: translateZ(0);
  backface-visibility: hidden;
  contain: layout style paint;
  will-change: transform;
}

.image-item:hover {
  background-color: var(--color-bg-hover);
  border-color: var(--color-border-primary);
  box-shadow: var(--shadow-elevated);
  z-index: 10;
  transform: translateY(-2px) translateZ(0);
}

.collapsed .image-item.selected, .image-item.selected {
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-elevated);
}

.collapsed .panel-content {
  max-height: calc(100vh - var(--header-height) - 57px);
  grid-template-columns: 1fr;
  gap: 0.75rem;
  padding: 0.75rem;
}

.collapsed .image-item {
  border-radius: 4px;
  aspect-ratio: 1;
  flex-direction: column;
  height: auto;
  width: 100%;
  border: 1px solid var(--status-color, var(--color-border-secondary));
  transition: border-width 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}

.collapsed .image-item:hover {
  border-width: 3px;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .image-item {
  flex-direction: column;
  width: 100%;
  height: fit-content;
}

.left-panel:not(.collapsed) .panel-content.list-view .image-item {
  flex-direction: row;
  aspect-ratio: auto;
  height: auto;
  padding: 0.75rem 1rem;
}

.image-placeholder {
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  contain: strict;
  transform: translateZ(0);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: none;
  display: block;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .thumbnail-image {
  object-fit: contain;
}

.collapsed .thumbnail-image {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .image-placeholder {
  width: 100%;
  aspect-ratio: 1;
  flex: 0 0 auto;
  max-height: 250px;
}

.collapsed .image-placeholder {
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  flex: 1;
}

.placeholder-icon {
  font-size: 2.5rem;
  opacity: 0.5;
  transition: font-size 0.3s ease;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .placeholder-icon {
  font-size: 3.5rem;
}

.collapsed .placeholder-icon {
  font-size: 2rem;
}

.left-panel:not(.collapsed) .panel-content.list-view .placeholder-icon {
  font-size: 2rem;
}

.image-info {
  padding: 0.75rem;
  color: var(--color-text-primary);
  flex-shrink: 0;
  background-color: transparent;
  contain: layout style;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .image-info {
  padding: 0.5rem 0.75rem;
  background-color: transparent;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  min-height: unset;
  margin-top: auto;
}

.left-panel:not(.collapsed) .panel-content.list-view .image-item .image-info {
  padding: 0;
}

.image-info-header {
  max-width: 60%;
  overflow: hidden;
}

.image-title {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  font-weight: var(--font-weight-extrabold);
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .image-title {
  font-size: 0.85rem;
  font-weight: var(--font-weight-bold);
  margin: 0 0 0.25rem 0;
  line-height: 1.2;
}

.left-panel:not(.collapsed) .panel-content.list-view .image-title {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-extrabold);
}

.image-description {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.8;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.image-time {
  margin: 0;
  font-size: 0.75rem;
  opacity: 0.6;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.left-panel:not(.collapsed) .panel-content.list-view .image-description {
  font-size: 0.9rem;
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.left-panel:not(.collapsed) .panel-content:not(.list-view) .image-description {
  font-size: 0.85rem;
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.left-panel:not(.collapsed) .panel-content.list-view .image-info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 0;
}

.ai-submit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
}

.ai-submit-btn {
  padding: 0.4rem 0.9rem;
  border: var(--border-width-standard) solid var(--color-accent-primary);
  border-radius: var(--border-radius-small);
  background-color: var(--color-accent-primary);
  color: var(--color-bg-primary);
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease;
}

.ai-submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ai-select-all {
  font-size: 0.8rem;
  color: var(--color-accent-primary);
  text-decoration: underline;
  white-space: nowrap;
}

.ai-select-all.muted {
  color: var(--color-text-secondary);
  text-decoration: none;
}

.ai-select-checkbox {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 3;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-accent-primary);
}

.status-badge {
  font-size: 10px;
  padding: 4px 8px;
  border-radius: 5px;
  color: #fff;
  font-weight: bold;
  position: absolute;
  top: 5px;
  left: 5px;
  z-index: 2;
  text-transform: capitalize;
}

.status-badge-list {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 4px;
  color: #fff;
  font-weight: var(--font-weight-semibold);
  display: inline-block;
  margin-top: 0.25rem;
  text-transform: capitalize;
}

.panel-content .favourite-icon {
  color: var(--color-error);
  font-size: 1rem;
}

.panel-content .favourite-icon.grid {
  position: absolute;
  bottom: 50%;
  transform: translateY(50%);
  right: 1rem;
}
</style>
